"use client";

import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import type { PlatformLocationsData } from "@/types/property";
import {
  CategoryPropertyTypeConfig,
  PropertyCategory,
  PropertyTypeLabels,
} from "@/types/property-config";
import {
  CATEGORY_LABELS,
  LISTING_TYPE_LABELS,
  buildListingPath,
  buildLocationPath,
} from "@/lib/seo/property-urls";

export interface ListingDirectoryFilterProps {
  listingType?: string;
  category?: PropertyCategory;
  propertyType?: string;
  stateSlug?: string;
  citySlug?: string;
  locations: PlatformLocationsData | null;
}

interface FilterValues {
  listingType?: string;
  category?: PropertyCategory;
  propertyType?: string;
  stateSlug?: string;
  citySlug?: string;
}

function categoryForPropertyType(
  propertyType: string,
): PropertyCategory | undefined {
  for (const [category, types] of Object.entries(CategoryPropertyTypeConfig)) {
    if ((types as string[]).includes(propertyType)) {
      return category as PropertyCategory;
    }
  }
  return undefined;
}

function SelectField({
  label,
  value,
  onChange,
  disabled,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-[46px] w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 text-sm text-slate-800 outline-none transition-colors focus:border-bnp-cyan focus:ring-1 focus:ring-bnp-cyan disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
      >
        {children}
      </select>
    </label>
  );
}

export default function ListingDirectoryFilter({
  listingType,
  category,
  propertyType,
  stateSlug,
  citySlug,
  locations,
}: ListingDirectoryFilterProps) {
  const router = useRouter();

  const states = [...(locations?.states || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const selectedState = states.find((state) => state.slug === stateSlug);
  const cities = [...(selectedState?.cities || [])].sort(
    (a, b) =>
      b.listing_count - a.listing_count || a.name.localeCompare(b.name),
  );
  const propertyTypes = category
    ? (CategoryPropertyTypeConfig[category] as string[])
    : (Object.keys(PropertyTypeLabels) as string[]);
  const hasFilters = Boolean(
    listingType || category || propertyType || stateSlug || citySlug,
  );

  const navigate = (changes: Partial<FilterValues>) => {
    const next: FilterValues = {
      listingType,
      category,
      propertyType,
      stateSlug,
      citySlug,
      ...changes,
    };

    const nextListingType =
      next.listingType || (next.category || next.propertyType ? "sale" : "");

    if (nextListingType) {
      router.push(
        buildListingPath({
          listingType: nextListingType,
          category: next.category,
          propertyType: next.propertyType,
          stateSlug: next.stateSlug,
          citySlug: next.citySlug,
        }),
      );
      return;
    }

    if (next.stateSlug) {
      router.push(buildLocationPath(next.stateSlug, next.citySlug));
      return;
    }

    router.push("/properties");
  };

  const handleListingTypeChange = (value: string) => {
    if (!value) {
      navigate({
        listingType: undefined,
        category: undefined,
        propertyType: undefined,
      });
      return;
    }

    navigate({ listingType: value });
  };

  const handleCategoryChange = (value: string) => {
    const nextCategory = value
      ? (value as PropertyCategory)
      : undefined;
    const nextPropertyType =
      nextCategory &&
      propertyType &&
      (CategoryPropertyTypeConfig[nextCategory] as string[]).includes(
        propertyType,
      )
        ? propertyType
        : undefined;

    navigate({
      category: nextCategory,
      propertyType: nextPropertyType,
      listingType: listingType || (nextCategory ? "sale" : undefined),
    });
  };

  const handlePropertyTypeChange = (value: string) => {
    const nextPropertyType = value || undefined;
    const nextCategory = nextPropertyType
      ? category || categoryForPropertyType(nextPropertyType)
      : category;

    navigate({
      category: nextCategory,
      propertyType: nextPropertyType,
      listingType: listingType || (nextPropertyType ? "sale" : undefined),
    });
  };

  const handleStateChange = (value: string) => {
    navigate({
      stateSlug: value || undefined,
      citySlug: undefined,
    });
  };

  const handleCityChange = (value: string) => {
    navigate({ citySlug: value || undefined });
  };

  return (
    <section
      aria-label="Filter property listings"
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold text-slate-900">
          Filter listings
        </h2>
        {hasFilters && (
          <button
            type="button"
            onClick={() => router.push("/properties")}
            className="text-sm font-medium text-teal-800 hover:text-teal-950 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SelectField
          label="Listing type"
          value={listingType || ""}
          onChange={handleListingTypeChange}
        >
          <option value="">Any</option>
          {Object.entries(LISTING_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Category"
          value={category || ""}
          onChange={handleCategoryChange}
        >
          <option value="">Any</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Property type"
          value={propertyType || ""}
          onChange={handlePropertyTypeChange}
        >
          <option value="">Any</option>
          {propertyTypes.map((value) => (
            <option key={value} value={value}>
              {PropertyTypeLabels[value as keyof typeof PropertyTypeLabels] ||
                value}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="State"
          value={stateSlug || ""}
          onChange={handleStateChange}
        >
          <option value="">Any</option>
          {states.map((state) => (
            <option key={state.slug} value={state.slug}>
              {state.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="City"
          value={citySlug || ""}
          onChange={handleCityChange}
          disabled={!stateSlug}
        >
          <option value="">
            {stateSlug ? "Any" : "Select a state first"}
          </option>
          {cities.map((city) => (
            <option key={city.slug} value={city.slug}>
              {city.name}
            </option>
          ))}
        </SelectField>
      </div>
    </section>
  );
}
